import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo } from "react";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { HelpMobileMenu } from "@/components/marketing/help-mobile-menu";
import {
  HELP_ARTICLES,
  HELP_CATEGORIES,
  articleSearchHaystack,
  groupByCategory,
  type HelpArticle,
} from "@/lib/help-content";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const INK = "#14213A";
const INK_2 = "#545C6B";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/help/")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Help center: CapitalDart" },
      {
        name: "description",
        content:
          "How CapitalDart works: getting started, running a deal, the Investor Readiness Report, the contact status legend, outreach and sending, credits, teams, data sources, and privacy.",
      },
      { property: "og:title", content: "Help center: CapitalDart" },
      {
        property: "og:description",
        content:
          "How CapitalDart works: getting started, running a deal, the Investor Readiness Report, the contact status legend, outreach and sending, credits, teams, data sources, and privacy.",
      },
      { property: "og:url", content: "https://capitaldart.com/help" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Help center: CapitalDart" },
      {
        name: "twitter:description",
        content:
          "How CapitalDart works: getting started, running a deal, the Investor Readiness Report, the contact status legend, outreach and sending, credits, teams, data sources, and privacy.",
      },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/help" }],
  }),
  component: HelpPage,
});

function HelpPage() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();

  const matches = useMemo<HelpArticle[]>(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return HELP_ARTICLES;
    return HELP_ARTICLES.filter((a) => articleSearchHaystack(a).includes(needle));
  }, [q]);

  const groups = useMemo(() => groupByCategory(matches), [matches]);
  const hasQuery = q.trim().length > 0;

  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <HelpNav />
      <section
        style={{
          background: PAPER_2,
          padding: "88px 0 48px",
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 18,
            }}
          >
            Help center
          </div>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(36px,4.2vw,56px)",
              lineHeight: 1.05,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            How CapitalDart works.
          </h1>
          <p
            style={{
              marginTop: 18,
              maxWidth: 680,
              color: INK_2,
              fontSize: 17,
              lineHeight: 1.6,
            }}
          >
            Short, direct articles on getting started, running a deal, the Investor Readiness
            Report, contact statuses, outreach, credits, teams, and privacy.
          </p>

          <div style={{ marginTop: 28, maxWidth: 560 }}>
            <label htmlFor="help-search" className="sr-only">
              Search the help center
            </label>
            <input
              id="help-search"
              type="search"
              value={q}
              placeholder="Search articles"
              onChange={(e) => {
                const value = e.target.value;
                void navigate({
                  to: "/help",
                  search: { q: value },
                  replace: true,
                });
              }}
              style={{
                width: "100%",
                background: PAPER,
                border: `1px solid ${LINE}`,
                borderRadius: 8,
                padding: "12px 14px",
                fontFamily: FONT_SANS,
                fontSize: 15,
                color: INK,
                outline: "none",
              }}
            />
          </div>
        </div>
      </section>

      <section style={{ padding: "56px 0 96px" }}>
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          {hasQuery && matches.length === 0 ? (
            <div
              style={{
                fontFamily: FONT_SANS,
                fontSize: 16,
                color: INK_2,
                padding: "32px 0",
              }}
            >
              No articles match <span style={{ color: INK, fontWeight: 600 }}>{q}</span>. Try a
              broader term, or{" "}
              <Link
                to="/help"
                search={{ q: "" }}
                style={{
                  color: INK,
                  borderBottom: `2px solid ${GOLD}`,
                  paddingBottom: 2,
                  textDecoration: "none",
                }}
              >
                clear the search
              </Link>
              .
            </div>
          ) : hasQuery ? (
            <FlatResults matches={matches} />
          ) : (
            <GroupedList groups={groups} />
          )}

          <div
            style={{
              marginTop: 48,
              display: "flex",
              flexWrap: "wrap",
              gap: 28,
              alignItems: "center",
            }}
          >
            <Link
              to="/faq"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              See the FAQ
            </Link>
            <Link
              to="/contact"
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: INK,
                borderBottom: `2px solid ${GOLD}`,
                paddingBottom: 3,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Still stuck? Contact us
            </Link>
          </div>
        </div>
      </section>

      <HelpFooter />
    </main>
  );
}

function GroupedList({ groups }: { groups: Array<{ heading: string; items: HelpArticle[] }> }) {
  return (
    <>
      {groups.map((g) => (
        <div key={g.heading} style={{ marginBottom: 48 }}>
          <h2
            style={{
              fontFamily: FONT_MONO,
              fontWeight: 500,
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD,
              margin: "0 0 18px",
              paddingBottom: 14,
              borderBottom: `2px solid ${GOLD}`,
            }}
          >
            {g.heading}
          </h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {g.items.map((a) => (
              <li key={a.slug} style={{ borderBottom: `1px solid ${LINE}`, padding: "20px 0" }}>
                <ArticleCard article={a} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function FlatResults({ matches }: { matches: HelpArticle[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {matches.map((a) => (
        <li key={a.slug} style={{ borderBottom: `1px solid ${LINE}`, padding: "20px 0" }}>
          <ArticleCard article={a} categoryLabel={a.category} />
        </li>
      ))}
    </ul>
  );
}

function ArticleCard({ article, categoryLabel }: { article: HelpArticle; categoryLabel?: string }) {
  return (
    <Link
      to="/help/$slug"
      params={{ slug: article.slug }}
      search={{}}
      style={{
        display: "block",
        textDecoration: "none",
        color: INK,
      }}
    >
      {categoryLabel && (
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 6,
          }}
        >
          {categoryLabel}
        </div>
      )}
      <div
        style={{
          fontFamily: FONT_SERIF,
          fontWeight: 600,
          fontSize: 20,
          lineHeight: 1.3,
          marginBottom: 6,
        }}
      >
        {article.title}
      </div>
      <div
        style={{
          fontFamily: FONT_SANS,
          fontSize: 15,
          lineHeight: 1.6,
          color: INK_2,
        }}
      >
        {article.summary}
      </div>
    </Link>
  );
}

function HelpNav() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <header
      style={{
        background: NAVY,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023.98px) {
          .cd-helpnav-desktop { display: none !important; }
          .cd-helpnav-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={34}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
        </Link>
        <nav
          className="cd-helpnav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {loading ? null : isAuthed ? (
            <Link
              to="/dashboard"
              style={{
                fontFamily: FONT_SANS,
                fontSize: 14,
                color: NAVY_TEXT_DIM,
                textDecoration: "none",
              }}
            >
              Back to dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                Back to home
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
        <HelpMobileMenu className="cd-helpnav-mobile" isAuthed={isAuthed} loading={loading} />
      </div>
    </header>
  );
}

function HelpFooter() {
  return (
    <footer
      style={{
        background: NAVY_DEEP,
        color: NAVY_TEXT,
        padding: "48px 0",
        textAlign: "center",
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200, padding: "0 32px" }}>
        <Logo
          src={LOGO_LIGHT}
          alt="CapitalDart"
          height={26}
          style={{
            display: "inline-block",
            marginBottom: 14,
          }}
          trademarkClassName="text-[#F4F1E8]"
        />
        <div
          style={{
            fontFamily: FONT_SANS,
            fontSize: 13,
            color: "#7E8A9C",
          }}
        >
          © {new Date().getFullYear()} CapitalDart. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
